import {FilterFn, Row} from "@tanstack/react-table";
import {getQuoteEndDate, getQuoteStartDate} from "./QuoteDates";

// Pulls the date a row should be filtered on. Defaults to the field named by the
// column id, which is what rows that carry their own start_date / end_date use.
type DateAccessor = (row: Row<any>, columnId: string) => string | undefined

const defaultDateAccessor: DateAccessor = (row, columnId) => row.original[columnId]

// A filter value of null reaches us when a date picker is cleared. Tanstack only drops
// undefined and empty string filters on its own, so guard here or a cleared picker
// becomes new Date(null), the 1970 epoch, and empties the table.
const toValidDate = (value: unknown) => {
    if (value === null || value === undefined) return undefined
    const date = new Date(value as Date)
    return isNaN(date.getTime()) ? undefined : date
}

// Custom filter functions for tables with start/end date values
export const makeStartDateFilter = (getDate: DateAccessor = defaultDateAccessor): FilterFn<any> => (
    row,
    columnId: string,
    filterValue: Date
) => {
    const startOfDay = toValidDate(filterValue)
    if (!startOfDay) return true
    startOfDay.setHours(0, 0, 0, 0)

    const rowDate = toValidDate(getDate(row, columnId))
    if (!rowDate) return false

    return rowDate >= startOfDay
}

export const makeEndDateFilter = (getDate: DateAccessor = defaultDateAccessor): FilterFn<any> => (
    row,
    columnId: string,
    filterValue: Date
) => {
    const endOfDay = toValidDate(filterValue)
    if (!endOfDay) return true
    endOfDay.setHours(23, 59, 59, 999)

    const rowDate = toValidDate(getDate(row, columnId))
    if (!rowDate) return false

    return rowDate <= endOfDay
}

export const startDateFilter: FilterFn<any> = makeStartDateFilter()

export const endDateFilter: FilterFn<any> = makeEndDateFilter()

// Quotes hold no dates themselves, they are filtered on the span of their events
export const quoteStartDateFilter: FilterFn<any> = makeStartDateFilter(row => getQuoteStartDate(row.original))

export const quoteEndDateFilter: FilterFn<any> = makeEndDateFilter(row => getQuoteEndDate(row.original))
